import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { gradeOfferingsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { enrollmentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import type { Enrollment } from '@/types/domain';

interface Props {
  open: boolean;
  enrollment: Enrollment;
  onClose: () => void;
  onTransferred: () => void;
}

/**
 * Moves a student to another section, or to another campus or grade in the same
 * academic year.
 *
 * This replaced an earlier "edit placement" form that overwrote the section in
 * place. The difference matters: the previous placement is now closed the day
 * before this one starts, so the class a child sat in last March is still
 * answerable rather than having been quietly replaced.
 *
 * Also used to assign a section for the first time, since a student is often
 * admitted to a grade before being placed in a class group.
 */
export function TransferModal({ open, enrollment, onClose, onTransferred }: Props) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [changeOffering, setChangeOffering] = useState(false);
  const [targetOfferingId, setTargetOfferingId] = useState(enrollment.gradeOfferingId);
  const [targetSectionId, setTargetSectionId] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ size: 200 }),
    queryFn: () => gradeOfferingsApi.list({ page: 0, size: 200 }),
    enabled: open && changeOffering,
  });

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ gradeOfferingId: targetOfferingId }),
    queryFn: () => sectionsApi.list({ page: 0, size: 100, gradeOfferingId: targetOfferingId }),
    enabled: open && targetOfferingId !== '',
  });

  const transfer = useMutation({
    mutationFn: () =>
      enrollmentsApi.transfer(enrollment.id, {
        // Sent only for a cross-offering move; omitting it keeps the enrolment
        // and changes just the placement beneath it.
        targetGradeOfferingId: changeOffering ? targetOfferingId : undefined,
        targetSectionId,
        rollNumber: rollNumber === '' ? undefined : rollNumber,
        effectiveDate,
        reason: reason === '' ? undefined : reason,
      }),
    onSuccess: () => {
      toast.success('Student transferred');
      void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });
      onTransferred();
    },
    onError: (error) => handleApiError(error),
  });

  const sameSection = targetSectionId === enrollment.sectionId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Transfer ${enrollment.studentName}`}
      description="The current placement closes the day before the new one begins, so no date has two answers."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={transfer.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => transfer.mutate()}
            loading={transfer.isPending}
            disabled={targetSectionId === '' || effectiveDate === '' || sameSection}
          >
            Transfer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-muted">
          Currently {enrollment.gradeName ?? 'unplaced'}
          {enrollment.sectionName !== undefined && ` · ${enrollment.sectionName}`}
          {enrollment.rollNumber !== undefined && ` · roll ${enrollment.rollNumber}`}
        </div>

        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 border-line text-accent focus:ring-accent"            checked={changeOffering}
            onChange={(event) => {
              setChangeOffering(event.target.checked);
              setTargetSectionId('');
              if (!event.target.checked) {
                setTargetOfferingId(enrollment.gradeOfferingId);
              }
            }}
          />
          <span>
            Move to a different campus or grade
            <span className="block text-xs text-subtle">
              Stays within the same academic year — moving up a year is a promotion.
            </span>
          </span>
        </label>

        {changeOffering && (
          <SelectField
            label="Grade offering"
            required
            value={targetOfferingId}
            onChange={(event) => {
              setTargetOfferingId(event.target.value);
              setTargetSectionId('');
            }}
          >
            <option value="">Select…</option>
            {offerings.data?.data
              .filter((offering) => !offering.historical)
              .map((offering) => (
                <option key={offering.id} value={offering.id}>
                  {offering.academicYear.name} · {offering.grade.name}
                  {offering.campusName !== undefined && ` · ${offering.campusName}`}
                </option>
              ))}
          </SelectField>
        )}

        <SelectField
          label="Section"
          required
          value={targetSectionId}
          onChange={(event) => setTargetSectionId(event.target.value)}
          error={sameSection ? 'The student is already in that section' : undefined}
        >
          <option value="">Select a section…</option>
          {sections.data?.data.map((section) => (
            <option key={section.id} value={section.id}>
              {section.name}
            </option>
          ))}
        </SelectField>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Effective date"
            type="date"
            required
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
          <TextField
            label="Roll number"
            value={rollNumber}
            onChange={(event) => setRollNumber(event.target.value)}
            hint="Optional; the old one is released"
          />
        </div>

        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="e.g. Balancing class sizes"
        />
      </div>
    </Modal>
  );
}
